import type {
  AccountLookupResponse,
  CreateInviteResponse,
  KeyPackageBundle,
  PushSubscribeRequest,
  PushTestResponse,
  RegisterRequest,
  RegisterResponse,
  TurnCredentialsResponse,
  VapidPublicKeyResponse,
} from "@truewave/protocol";
import { bs } from "./bytes.js";
import { config } from "./config.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

interface Creds {
  userId: string;
  token: string;
}

async function request<T>(
  path: string,
  init: RequestInit & { creds?: Creds } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set("content-type", "application/json");
  if (init.creds) {
    headers.set("authorization", `Bearer ${init.creds.token}`);
    headers.set("x-tw-user", init.creds.userId);
  }
  const res = await fetch(config.restUrl(path), { ...init, headers });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const e = body as { error?: string; message?: string } | undefined;
    throw new ApiError(res.status, e?.error ?? "error", e?.message ?? res.statusText);
  }
  return body as T;
}

export const api = {
  health: () => request<{ ok: boolean }>("/healthz"),

  register: (req: RegisterRequest) =>
    request<RegisterResponse>("/v1/accounts", { method: "POST", body: JSON.stringify(req) }),

  me: (creds: Creds) =>
    request<{ userId: string; username: string; keyPackagesAvailable: number; replenishBelow: number }>(
      "/v1/me",
      { creds },
    ),

  publishKeyPackages: (creds: Creds, keyPackages: string[]) =>
    request<{ available: number }>("/v1/key-packages", {
      method: "POST",
      body: JSON.stringify({ keyPackages }),
      creds,
    }),

  claimKeyPackage: (creds: Creds, userId: string) =>
    request<KeyPackageBundle>(`/v1/key-packages/${encodeURIComponent(userId)}`, { creds }),

  createInvite: (creds: Creds) =>
    request<CreateInviteResponse>("/v1/invites", { method: "POST", creds }),

  resolveInvite: (token: string) =>
    request<KeyPackageBundle>(`/v1/invites/${encodeURIComponent(token)}`),

  turnCredentials: (creds: Creds) =>
    request<TurnCredentialsResponse>("/v1/turn-credentials", { creds }),

  lookupAccount: (creds: Creds, userId: string) =>
    request<AccountLookupResponse>(`/v1/accounts/${encodeURIComponent(userId)}`, { creds }),

  vapidPublicKey: () => request<VapidPublicKeyResponse>("/v1/push/vapid-public-key"),

  pushSubscribe: (creds: Creds, sub: PushSubscribeRequest) =>
    request<void>("/v1/push/subscribe", { method: "POST", body: JSON.stringify(sub), creds }),

  pushTest: (creds: Creds) => request<PushTestResponse>("/v1/push/test", { method: "POST", creds }),

  pushUnsubscribe: (creds: Creds, endpoint: string) =>
    request<void>("/v1/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint }), creds }),

  /** Uploads ciphertext bytes for a peer to fetch once. Not JSON — raw body, own content-type. */
  async uploadAttachment(
    creds: Creds,
    recipientUserId: string,
    mimeType: string,
    ciphertext: Uint8Array,
  ): Promise<{ attachmentId: string; expiresAt: number }> {
    const res = await fetch(
      config.restUrl(`/v1/attachments?to=${encodeURIComponent(recipientUserId)}`),
      {
        method: "POST",
        headers: {
          "content-type": mimeType || "application/octet-stream",
          authorization: `Bearer ${creds.token}`,
          "x-tw-user": creds.userId,
        },
        body: bs(ciphertext),
      },
    );
    if (!res.ok) {
      const e = (await res.json().catch(() => undefined)) as { error?: string; message?: string } | undefined;
      throw new ApiError(res.status, e?.error ?? "error", e?.message ?? res.statusText);
    }
    return (await res.json()) as { attachmentId: string; expiresAt: number };
  },

  /** Fetches and consumes an attachment (the relay deletes its copy on this call). */
  async fetchAttachment(creds: Creds, attachmentId: string): Promise<Uint8Array> {
    const res = await fetch(config.restUrl(`/v1/attachments/${encodeURIComponent(attachmentId)}`), {
      headers: { authorization: `Bearer ${creds.token}`, "x-tw-user": creds.userId },
    });
    if (!res.ok) {
      const e = (await res.json().catch(() => undefined)) as { error?: string; message?: string } | undefined;
      throw new ApiError(res.status, e?.error ?? "error", e?.message ?? res.statusText);
    }
    return new Uint8Array(await res.arrayBuffer());
  },
};
